import { DISCORD_URL } from "@kitsulab/schema";
import { HostedDemoGuide } from "./HostedDemoGuide";
import {
	CheckCircle2,
	ExternalLink,
	LoaderCircle,
	PackagePlus,
	PlayCircle,
	PlugZap,
	Sparkles,
	X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { KitsuContentChoice, KitsuStatus, KitsuStep } from "@kitsulab/schema";
import { apiClient } from "@/lib/apiClient";
import { toastApiError } from "@/lib/apiErrors";
import { createKitsuItem, createKitsuProject, trackKitsuEvent, updateKitsuProgress, type KitsuEventName } from "@/lib/kitsu";
import { useAuth } from "@/components/AuthContext";
import { useProjectDataOptional } from "@/hooks/useProjectData";


const PLUGIN_URL =
	"https://create.roblox.com/store/asset/118698231691748/Kitsulab-Studio-Manager?keyword=kitsulab&pageNumber=1&pagePosition=0";
const ROBLOX_STUDIO_URL = "https://www.roblox.com/create";

type KitsuState = {
	status: KitsuStatus;
	step: KitsuStep;
	project: { id: string; slug: string; name: string } | null;
	item: { id: string; name: string } | null;
	studioInstalled: boolean | null;
	firstContentChoice: KitsuContentChoice | null;
	startedAt: string | null;
	lastStep: string | null;
	lastEvent: string | null;
	lastEventAt: string | null;
	completedAt: string | null;
};

type CreatedItem = { id: string; name: string };
type ChatMessage = { id: string; side: "assistant" | "user"; text: string };

const LEGACY_CHAT_STORAGE_KEY = "kitsulab:kitsu-chat-v1";

const STEP_SHOWN_EVENT: Partial<Record<KitsuStep, KitsuEventName>> = {
	studio: "kitsu_studio_question_shown",
	project: "kitsu_project_name_step_shown",
	content: "kitsu_first_content_step_shown",
	pairing: "kitsu_plugin_step_shown",
	sync: "kitsu_sync_step_shown",
	linking: "kitsu_linking_step_shown",
	runtime: "kitsu_play_step_shown",
};

const ITEM_NAME_KEY: Record<KitsuContentChoice, string> = {
	npc: "kitsu.content.npcName",
	item: "kitsu.content.itemName",
	blank: "kitsu.content.blankName",
};

function eventStep(state: KitsuState): KitsuStep {
	return state.status === "NOT_STARTED" && state.step === "studio"
		? "welcome"
		: state.step;
}

function readChatMessages(storageKey: string | null): ChatMessage[] {
	if (!storageKey) return [];
	try {
		const stored = window.sessionStorage.getItem(storageKey);
		if (!stored) return [];
		const parsed: unknown = JSON.parse(stored);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((message): message is ChatMessage =>
			typeof message === "object" &&
			message !== null &&
			"id" in message &&
			"side" in message &&
			"text" in message &&
			typeof message.id === "string" &&
			(message.side === "assistant" || message.side === "user") &&
			typeof message.text === "string",
		);
	} catch {
		return [];
	}
}

export function Kitsu({ projectSlug, startChoices = false }: { projectSlug?: string; startChoices?: boolean }) {
	async function startProject() {
		await trackKitsuEvent({ eventName: "kitsu_project_selected", step: "welcome" });
		const { data } = await apiClient.get<KitsuState>("/api/kitsu");
		if (data.status === "NOT_STARTED") {
			await updateKitsuProgress({ eventName: "kitsu_started", step: "welcome" });
		} else if (data.status === "DISMISSED") {
			await updateKitsuProgress({ eventName: "kitsu_resumed", step: data.step });
		}
	}
	return (
		<HostedDemoGuide projectSlug={projectSlug} startChoices={startChoices} onStartProject={startProject}>
			<StudioKitsu projectSlug={projectSlug} />
		</HostedDemoGuide>
	);
}

function StudioKitsu({ projectSlug }: { projectSlug?: string }) {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const projectData = useProjectDataOptional();
	const chatStorageKey = user ? `kitsulab:kitsu-chat-v1:${user.id}` : null;
	const tutorialUrl = i18n.language.startsWith("fr")
		? "/fr/tutorials/"
		: "/tutorials/";
	const docsUrl = i18n.language.startsWith("fr") ? "/fr/tutorials/getting-started/" : "/tutorials/getting-started/";
	const [state, setState] = useState<KitsuState | null>(null);
	const [projectName, setProjectName] = useState("");
	const [creatingProject, setCreatingProject] = useState(false);
	const [creatingContent, setCreatingContent] = useState(false);
	const [createdItem, setCreatedItem] = useState<CreatedItem | null>(null);
	const [showCompletion, setShowCompletion] = useState(false);
	const [checkingLink, setCheckingLink] = useState(false);
	const [linkNotReady, setLinkNotReady] = useState(false);
	const progressPending = useRef(false);
	const loadSequence = useRef(0);
	const [checkingRuntime, setCheckingRuntime] = useState(false);
	const [showRuntimeProblem, setShowRuntimeProblem] = useState(false);
	const [runtimeProblemDescription, setRuntimeProblemDescription] = useState("");
	const [creatingRuntimeTicket, setCreatingRuntimeTicket] = useState(false);
	const [runtimeTicketCreated, setRuntimeTicketCreated] = useState(false);
	const [messages, setMessages] = useState<ChatMessage[]>(() => readChatMessages(chatStorageKey));
	const shown = useRef(new Set<string>());
	const previousStatus = useRef<KitsuStatus | null>(null);

	useEffect(() => {
		setMessages(readChatMessages(chatStorageKey));
		try { window.sessionStorage.removeItem(LEGACY_CHAT_STORAGE_KEY); } catch { /* Storage can be disabled by the browser. */ }
	}, [chatStorageKey]);

	useEffect(() => {
		if (!chatStorageKey) return;
		try { window.sessionStorage.setItem(chatStorageKey, JSON.stringify(messages)); } catch { /* Storage can be disabled by the browser. */ }
	}, [chatStorageKey, messages]);

	const addMessage = useCallback((side: ChatMessage["side"], text: string) => {
		setMessages((current) => [
			...current,
			{ id: crypto.randomUUID(), side, text },
		]);
	}, []);

	const addUserMessage = useCallback((text: string) => {
		addMessage("user", text);
	}, [addMessage]);

	const addAssistantMessage = useCallback((title: string, description: string) => {
		addMessage("assistant", `${title}\n${description}`);
	}, [addMessage]);

	const load = useCallback(async () => {
		const sequence = ++loadSequence.current;
		const query = projectSlug ? `?projectSlug=${encodeURIComponent(projectSlug)}` : "";
		try {
			const response = await apiClient.get<KitsuState>(`/api/kitsu${query}`);
			if (sequence !== loadSequence.current) return null;
			if (previousStatus.current !== null && previousStatus.current !== "COMPLETED" && response.data.status === "COMPLETED") {
				setShowCompletion(true);
			}
			previousStatus.current = response.data.status;
			setState(response.data);
			return response.data;
		} catch {
			return null;
		}
	}, [projectSlug]);

	const track = useCallback((
		eventName: KitsuEventName,
		step: KitsuStep,
		properties?: { contentType?: KitsuContentChoice; studioInstalled?: boolean },
		slug = state?.project?.slug ?? projectSlug,
	) => {
		return trackKitsuEvent({
			eventName,
			step,
			projectSlug: slug,
			properties,
		});
	}, [projectSlug, state?.project?.slug]);

	useEffect(() => {
		load();
		window.addEventListener("kitsulab:onboarding-update", load);
		window.addEventListener("focus", load);
		return () => {
			loadSequence.current += 1;
			window.removeEventListener("kitsulab:onboarding-update", load);
			window.removeEventListener("focus", load);
		};
	}, [load]);

	useEffect(() => {
		const handleItemSaved = (event: Event) => {
			if (!createdItem) return;
			const detail = (event as CustomEvent<unknown>).detail;
			if (
				typeof detail !== "object" ||
				detail === null ||
				!("itemId" in detail) ||
				!("name" in detail) ||
				detail.itemId !== createdItem.id ||
				typeof detail.name !== "string"
			) return;
			addAssistantMessage(t("kitsu.created.title"), t("kitsu.created.description"));
			addUserMessage(detail.name);
			setCreatedItem(null);
			load();
		};
		window.addEventListener("kitsulab:kitsu-item-named", handleItemSaved);
		return () => window.removeEventListener("kitsulab:kitsu-item-named", handleItemSaved);
	}, [addAssistantMessage, addUserMessage, createdItem, load, t]);

	useEffect(() => {
		if (!state || state.status === "DISMISSED" || state.status === "COMPLETED") return;
		const step = eventStep(state);
		const key = `${state.status}:${step}:${state.project?.id ?? "account"}`;
		if (shown.current.has(key)) return;
		shown.current.add(key);
		void track("kitsu_shown", step);
		const eventName = STEP_SHOWN_EVENT[step];
		if (eventName) void track(eventName, step);
	}, [state, track]);

	async function changeProgress(eventName: KitsuEventName, step: KitsuStep, properties?: { studioInstalled: boolean }) {
		if (progressPending.current) return;
		progressPending.current = true;
		try {
			await updateKitsuProgress({ eventName, step, properties, projectSlug: state?.project?.slug ?? projectSlug });
			await load();
		} catch (error) {
			toastApiError(error, t("kitsu.progressError"));
		} finally {
			progressPending.current = false;
		}
	}

	async function checkLink() {
		if (checkingLink) return;
		setCheckingLink(true);
		setLinkNotReady(false);
		try {
			const next = await load();
			if (!next) throw new Error("Kitsu state unavailable");
			setLinkNotReady(next.step === "linking");
		} catch (error) {
			toastApiError(error, t("kitsu.progressError"));
		} finally {
			setCheckingLink(false);
		}
	}

	async function start() {
		addAssistantMessage(t("kitsu.welcome.title"), t("kitsu.welcome.description"));
		addUserMessage(t("kitsu.welcome.cta"));
		await changeProgress("kitsu_started", "welcome");
	}

	async function resume() {
		addUserMessage(t("kitsu.resume"));
		await changeProgress("kitsu_resumed", state?.step ?? "welcome");
	}

	async function dismiss() {
		if (!state) return;
		await changeProgress("kitsu_dismissed", eventStep(state));
	}

	async function confirmRuntime() {
		setCheckingRuntime(true);
		try {
			addAssistantMessage(t("kitsu.runtime.title"), t("kitsu.runtime.description"));
			addUserMessage(t("kitsu.runtime.done"));
			await changeProgress("kitsu_runtime_confirmed", "runtime");
		} finally {
			setCheckingRuntime(false);
		}
	}

	function beginRuntimeProblem() {
		addAssistantMessage(t("kitsu.runtime.title"), t("kitsu.runtime.description"));
		addUserMessage(t("kitsu.runtime.problem"));
		setRuntimeProblemDescription("");
		setRuntimeTicketCreated(false);
		setShowRuntimeProblem(true);
	}

	async function createRuntimeTicket(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!state || !runtimeProblemDescription.trim() || creatingRuntimeTicket) return;
		setCreatingRuntimeTicket(true);
		try {
			await apiClient.post("/api/support/tickets", {
				title: t("kitsu.runtime.ticketTitle"),
				category: "bug",
				priority: "high",
				projectId: state.project?.id ?? null,
				sourceUrl: window.location.href,
				body: runtimeProblemDescription.trim(),
			});
			addUserMessage(runtimeProblemDescription.trim());
			addAssistantMessage(t("kitsu.runtime.ticketCreated"), "");
			setRuntimeTicketCreated(true);
		} catch (error) {
			toastApiError(error, t("kitsu.runtime.ticketError"));
		} finally {
			setCreatingRuntimeTicket(false);
		}
	}

	async function answerStudio(installed: boolean) {
		if (!state) return;
		addAssistantMessage(
			t("kitsu.studio.title"),
			state.studioInstalled === false
				? t("kitsu.studio.notYet")
				: t("kitsu.studio.description"),
		);
		addUserMessage(t(installed ? "kitsu.studio.yes" : "kitsu.studio.no"));
		await changeProgress(
			installed ? "kitsu_studio_yes" : "kitsu_studio_no",
			"studio",
			{ studioInstalled: installed },
		);
	}

	async function createProject(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!projectName.trim() || creatingProject) return;
		setCreatingProject(true);
		try {
			addAssistantMessage(t("kitsu.project.title"), t("kitsu.project.description"));
			addUserMessage(projectName.trim());
			const project = await createKitsuProject(projectName.trim());
			void navigate(`/projects/${project.slug}/items`);
		} catch (error) {
			toastApiError(error, t("operationErrors.createProject"));
		} finally {
			setCreatingProject(false);
		}
	}

	async function createContent(choice: KitsuContentChoice) {
		if (!state?.project || creatingContent) return;
		setCreatingContent(true);
		try {
			addAssistantMessage(t("kitsu.content.title"), t("kitsu.content.description"));
			addUserMessage(t(`kitsu.content.${choice}`));
			const item = await createKitsuItem(state.project.slug, choice, t(ITEM_NAME_KEY[choice]));
			if (projectData?.slug === state.project.slug) {
				projectData.upsert("items", item);
			}
			setCreatedItem({ id: item.id, name: item.name });
			void navigate(`/projects/${state.project.slug}/items/${item.id}?kitsuFocus=name`);
			window.dispatchEvent(new Event("kitsulab:onboarding-update"));
		} catch (error) {
			toastApiError(error, t("operationErrors.saveItem"));
		} finally {
			setCreatingContent(false);
		}
	}

	if (!state) return null;
	if (showCompletion) {
		return (
			<KitsuCard history={messages} onDismiss={() => setShowCompletion(false)}>
				<CheckCircle2 size={19} />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-bold">{t("kitsu.completed.title")}</p>
					<p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.completed.description")}</p>
					<p className="mt-2 text-xs" style={{ color: "#c4b5fd" }}>{t("kitsu.completed.next")}</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<button type="button" onClick={() => void navigate(`/projects/${state.project?.slug}/items?create=1`)} className="btn-pill btn-pill-primary inline-flex items-center gap-2 text-sm"><PackagePlus size={14} />{t("kitsu.completed.createItem")}</button>
					<a href={tutorialUrl} target="_blank" rel="noreferrer" className="btn-pill btn-pill-secondary inline-flex items-center gap-2 text-sm"><ExternalLink size={14} />{t("kitsu.completed.video")}</a>
					<a href={DISCORD_URL} target="_blank" rel="noreferrer" className="btn-pill btn-pill-secondary inline-flex items-center gap-2 text-sm"><ExternalLink size={14} />{t("kitsu.completed.discord")}</a>
				</div>
			</KitsuCard>
		);
	}
	if (state.status === "COMPLETED") return null;
	if (state.status === "DISMISSED") {
		return (
			<div className="fixed bottom-4 right-4 z-50">
				<button
					type="button"
					onClick={() => void resume()}
					className="btn-pill btn-pill-secondary inline-flex items-center gap-2 px-4 py-3 text-sm shadow-2xl"
					style={{ background: "#18181b", borderColor: "rgba(139,92,246,0.45)" }}
				>
					<Sparkles size={14} />
					{t("kitsu.resume")}
				</button>
			</div>
		);
	}

	const step = eventStep(state);
	if (createdItem) {
		return (
			<KitsuCard history={messages} onDismiss={() => void dismiss()}>
				<Sparkles size={19} />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-bold">{t("kitsu.created.title")}</p>
					<p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.created.description")}</p>
					<p className="mt-2 text-xs" style={{ color: "#c4b5fd" }}>{t("kitsu.created.manual")}</p>
				</div>
				<button type="button" onClick={() => { addAssistantMessage(t("kitsu.created.title"), t("kitsu.created.description")); addUserMessage(t("kitsu.created.cta", { name: createdItem.name })); void navigate(`/projects/${state.project?.slug}/items/${createdItem.id}?kitsuFocus=name`); }} className="btn-pill btn-pill-primary shrink-0 text-sm">
					{t("kitsu.created.cta", { name: createdItem.name })}
				</button>
			</KitsuCard>
		);
	}

	if (step === "welcome") {
		return (
			<KitsuCard history={messages} onDismiss={() => void dismiss()}>
				<Sparkles size={19} />
				<div className="min-w-0 flex-1"><p className="text-sm font-bold">{t("kitsu.welcome.title")}</p><p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.welcome.description")}</p></div>
				<button type="button" onClick={() => void start()} className="btn-pill btn-pill-primary shrink-0 text-sm">{t("kitsu.welcome.cta")}</button>
			</KitsuCard>
		);
	}
	if (step === "studio") {
			return (
				<KitsuCard history={messages} onDismiss={() => void dismiss()}><PlugZap size={19} /><div className="min-w-0 flex-1"><p className="text-sm font-bold">{t("kitsu.studio.title")}</p><p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{state.studioInstalled === false ? t("kitsu.studio.notYet") : t("kitsu.studio.description")}</p></div>{state.studioInstalled === false ? <div className="flex shrink-0 flex-wrap gap-2"><a href={ROBLOX_STUDIO_URL} target="_blank" rel="noreferrer" onClick={() => { addAssistantMessage(t("kitsu.studio.title"), t("kitsu.studio.notYet")); addUserMessage(t("kitsu.studio.download")); void track("kitsu_studio_download_clicked", "studio"); }} className="btn-pill btn-pill-secondary inline-flex items-center gap-1 text-sm"><ExternalLink size={14} />{t("kitsu.studio.download")}</a><button type="button" onClick={() => void answerStudio(true)} className="btn-pill btn-pill-primary text-sm">{t("kitsu.studio.installed")}</button></div> : <div className="flex shrink-0 gap-2"><button type="button" onClick={() => void answerStudio(true)} className="btn-pill btn-pill-primary text-sm">{t("kitsu.studio.yes")}</button><button type="button" onClick={() => void answerStudio(false)} className="btn-pill btn-pill-secondary text-sm">{t("kitsu.studio.no")}</button></div>}</KitsuCard>
			);
	}
	if (step === "project") {
		return (
			<KitsuCard history={messages} onDismiss={() => void dismiss()}><Sparkles size={19} /><form onSubmit={createProject} className="flex min-w-0 flex-1 flex-col gap-2 md:flex-row md:items-center"><div className="min-w-0 flex-1"><p className="text-sm font-bold">{t("kitsu.project.title")}</p><p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.project.description")}</p></div><input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder={t("kitsu.project.placeholder")} className="rounded-lg px-3 py-2 text-sm outline-none" style={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", color: "#e4e4e7" }} /><button type="submit" disabled={creatingProject} className="btn-pill btn-pill-primary inline-flex shrink-0 items-center gap-2 text-sm">{creatingProject && <LoaderCircle size={14} className="animate-spin" />}<span>{t("kitsu.project.cta")}</span></button></form></KitsuCard>
		);
	}
	if (step === "content") {
		return (
			<KitsuCard history={messages} onDismiss={() => void dismiss()}><PackagePlus size={19} /><div className="min-w-0 flex-1"><p className="text-sm font-bold">{t("kitsu.content.title")}</p><p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.content.description")}</p></div><div className="flex shrink-0 flex-wrap gap-2">{(["npc", "item", "blank"] as KitsuContentChoice[]).map((choice) => <button key={choice} type="button" disabled={creatingContent} onClick={() => void createContent(choice)} className="btn-pill btn-pill-primary text-sm">{creatingContent ? <LoaderCircle size={14} className="animate-spin" /> : null}<span>{t(`kitsu.content.${choice}`)}</span></button>)}</div></KitsuCard>
		);
	}
	if (step === "pairing") {
		return <KitsuCard history={messages} onDismiss={() => void dismiss()}><PlugZap size={19} /><div className="min-w-0 flex-1"><p className="text-sm font-bold">{t("kitsu.pairing.title")}</p><p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.pairing.description")}</p></div><div className="flex flex-wrap gap-2"><a href={PLUGIN_URL} target="_blank" rel="noreferrer" onClick={() => { addAssistantMessage(t("kitsu.pairing.title"), t("kitsu.pairing.description")); addUserMessage(t("kitsu.pairing.cta")); void track("kitsu_plugin_store_clicked", "pairing"); }} className="btn-pill btn-pill-primary inline-flex shrink-0 items-center gap-2 text-sm"><ExternalLink size={14} />{t("kitsu.pairing.cta")}</a><a href={docsUrl} target="_blank" rel="noreferrer" className="btn-pill btn-pill-secondary inline-flex items-center gap-2 text-sm"><ExternalLink size={14} />{t("kitsu.pairing.help")}</a></div></KitsuCard>;
	}
	if (step === "sync") {
		return <KitsuCard history={messages} onDismiss={() => void dismiss()}><PlugZap size={19} /><div className="min-w-0 flex-1"><p className="text-sm font-bold">{t("kitsu.sync.title")}</p><p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.sync.description")}</p></div></KitsuCard>;
	}
	if (step === "linking") {
		return (
			<KitsuCard history={messages} onDismiss={() => void dismiss()}>
				<PlugZap size={19} />
				<p className="text-sm font-bold">{t("kitsu.linking.title", { name: state.item?.name ?? t("kitsu.content.blankName") })}</p>
				<p className="text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.linking.description")}</p>
				{linkNotReady && <p role="status" className="text-sm text-amber-300">{t("kitsu.linking.notReady")}</p>}
				<div className="flex flex-wrap gap-2">
					<a href={docsUrl} target="_blank" rel="noreferrer" className="btn-pill btn-pill-secondary inline-flex items-center gap-2 text-sm"><ExternalLink size={14} />{t("kitsu.linking.help")}</a>
					<button type="button" disabled={checkingLink} onClick={() => void checkLink()} className="btn-pill btn-pill-primary inline-flex items-center gap-2 text-sm">{checkingLink && <LoaderCircle size={14} className="animate-spin" />}<span>{t("kitsu.linking.done")}</span></button>
				</div>
			</KitsuCard>
		);
	}
	return (
		<KitsuCard history={messages} onDismiss={() => void dismiss()}>
			<PlayCircle size={19} />
			<div className="min-w-0 flex-1">
				<p className="text-sm font-bold">{t("kitsu.runtime.title")}</p>
				<p className="mt-1 text-sm" style={{ color: "#a1a1aa" }}>{t("kitsu.runtime.description")}</p>
			</div>
			{showRuntimeProblem ? (
				<form onSubmit={createRuntimeTicket} className="rounded-xl p-3 text-sm" style={{ background: "rgba(255,255,255,0.04)", color: "#d4d4d8" }}>
					<p>{runtimeTicketCreated ? t("kitsu.runtime.ticketCreated") : t("kitsu.runtime.problemPrompt")}</p>
					{!runtimeTicketCreated && <textarea value={runtimeProblemDescription} onChange={(event) => setRuntimeProblemDescription(event.target.value)} placeholder={t("kitsu.runtime.problemPlaceholder")} maxLength={10000} rows={4} className="mt-3 w-full resize-y rounded-lg px-3 py-2 text-sm outline-none" style={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", color: "#e4e4e7" }} />}
					<div className="mt-3 flex flex-wrap gap-2">
						{runtimeTicketCreated ? <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="btn-pill btn-pill-primary inline-flex items-center gap-2 text-sm"><ExternalLink size={14} />{t("kitsu.runtime.discord")}</a> : <button type="submit" disabled={creatingRuntimeTicket || !runtimeProblemDescription.trim()} className="btn-pill btn-pill-primary inline-flex items-center gap-2 text-sm">{creatingRuntimeTicket && <LoaderCircle size={14} className="animate-spin" />}<span>{t("kitsu.runtime.sendTicket")}</span></button>}
					</div>
				</form>
			) : (
				<div className="flex flex-wrap gap-2">
					<button type="button" disabled={checkingRuntime} onClick={() => void confirmRuntime()} className="btn-pill btn-pill-primary inline-flex items-center gap-2 text-sm">
						{checkingRuntime && <LoaderCircle size={14} className="animate-spin" />}
						<span>{t("kitsu.runtime.done")}</span>
					</button>
					<button type="button" onClick={beginRuntimeProblem} className="btn-pill btn-pill-secondary text-sm">{t("kitsu.runtime.problem")}</button>
				</div>
			)}
		</KitsuCard>
	);
}

function KitsuCard({ children, history, onDismiss }: { children: React.ReactNode; history: ChatMessage[]; onDismiss: () => void }) {
	const { t } = useTranslation();
	return (
		<section
			className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] max-w-[560px]"
			aria-live="polite"
		>
			<div
				className="glow-card overflow-hidden shadow-2xl"
				style={{
					background: "#141415",
					borderColor: "rgba(139,92,246,0.45)",
					boxShadow: "0 18px 56px rgba(0,0,0,0.58)",
				}}
			>
				<div
					className="flex items-center gap-2.5 px-4 py-3"
					style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
				>
					<div
						className="flex h-8 w-8 items-center justify-center rounded-full"
						style={{ background: "rgba(139,92,246,0.16)", color: "#a78bfa" }}
					>
						<Sparkles size={16} />
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-bold" style={{ color: "#e4e4e7" }}>Kitsu</p>
						<p className="text-[11px]" style={{ color: "#a78bfa" }}>{t("kitsu.chatStatus")}</p>
					</div>
					<button
						type="button"
						onClick={onDismiss}
						className="rounded-md p-1.5 transition-colors hover:bg-white/5"
						style={{ color: "#71717a" }}
						aria-label={t("common.close")}
					>
						<X size={16} />
					</button>
				</div>
				<div className="flex max-h-[min(420px,calc(100vh-7rem))] flex-col gap-3 overflow-y-auto p-4">
					<div className="flex shrink-0 flex-col gap-3 rounded-2xl p-3" style={{ background: "#232326", borderBottomLeftRadius: 4, color: "#a78bfa" }}>
						{children}
					</div>
					{history.length > 0 && <details className="shrink-0 text-sm" style={{ color: "#a1a1aa" }}>
						<summary className="cursor-pointer">{t("kitsu.history")}</summary>
						<div className="mt-3 flex flex-col gap-3">
							{history.map((message) => <p key={message.id} className={`max-w-full whitespace-pre-line break-words rounded-xl px-3 py-2 ${message.side === "user" ? "self-end" : "self-start"}`} style={{ background: message.side === "user" ? "#6d5dfc" : "#232326", color: "#e4e4e7" }}>{message.text}</p>)}
						</div>
					</details>}
				</div>
			</div>
		</section>
	);
}
